import type { Logger } from "@expert/logging";

import { useSendExpertAssistMessage } from "@asurion-core/expert-workspace-timeline";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useIsVerificationComplete } from "@expert/sales-tools";
import { useGuideManager, useGuideSession } from "@expert/solve-tools";
import { useCallback, useMemo } from "react";

import type { InlineGuide } from "@/sidecar/AssistTab";

import { env } from "@/config";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { useGetQueriesBySearchType, usePartnerConfig } from "@/partner/usePartnerConfig";
import { isVoiceTask, useActiveTask, usePartner } from "@/sdk";
import { userCache } from "@/shared-utils";
import { sidecarTabEventBus } from "@/sidecar/sidecarTabEventBus";

import { useAutomaticGuide } from "./useAutomaticGuide";
import { useGuideDeviceInfo } from "./useGuideDeviceInfo";
import { useGuideRecommendation } from "./useGuideRecommendation";

// Bring the AI Assist tab to the front when the expert clicks "Ask Assist" in a guide, so the response
// they just triggered is visible. The sidecar tab bus lives in the app, so the guide package (which
// can't import it) receives this as a callback.
const focusAssistTab = () => sidecarTabEventBus.emit("focus-tab", "ai-assist");

interface PlatformGuideManagerProps {
    isCustomerSession: boolean;
    sessionId: string;
    logger: Logger;
    /**
     * False hides guided flows, the Toolbox launch, and auto-opens (redesigned chat with expert assist off).
     * Only applies to the inline AI Assist guide; the Guided flows tab shows guides either way.
     */
    areGuidedFlowsAllowed: boolean;
}

export function usePlatformGuideManager({
    isCustomerSession,
    sessionId,
    logger: loggerProp,
    areGuidedFlowsAllowed,
}: Readonly<PlatformGuideManagerProps>) {
    const partner = usePartner();
    const activeTask = useActiveTask();
    const activeVoiceTask = useMemo(() => (isVoiceTask(activeTask) ? activeTask : null), [activeTask]);
    const { solveGuidedFlows, gaiaPayloadProps } = usePartnerConfig();
    const {
        features: { customFlowSystemId, isAiAssistGuidedFlowEnabled, isSolveGuidedFlowsEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { employeeId } = userCache;

    const globalConfig = useMemo(
        () => ({
            customFlowSystemId: customFlowSystemId ?? "",
            widgetId: solveGuidedFlows?.widgetId ?? "",
        }),
        [customFlowSystemId, solveGuidedFlows?.widgetId],
    );

    const logger = useMemo(
        () =>
            loggerProp.child({
                globalConfig,
                module: "usePlatformGuideManager",
                partner,
                sessionId,
                supportTeam: "solve",
            }),
        [globalConfig, loggerProp, partner, sessionId],
    );

    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const isCustomerVerificationComplete = useIsVerificationComplete(sessionId);
    const ssoAccessToken = userCache.ssoAccessToken ?? "";
    const clientChannelId = useGetQueriesBySearchType("QueryFindAgreementsByMDN")?.clientChannelId;

    // Deliberately unfiltered. The partner blocklists exist to stop unusable values reaching the
    // partner notes API; the guides look agreements up by MDN, and blanking it would change which
    // guides launch. That belongs in its own change with its own review, not this fix.
    // See `session-summary/summaryDispatchGate.ts`.
    const taskMdn = useTaskMdn({
        blocklistedNumbers: undefined,
        blocklistedPrefixes: undefined,
    });

    useGuideDeviceInfo({ logger, sessionId, gaiaPayloadProps });

    const callSid = activeVoiceTask?.agentCallId;

    const areGuidedFlowsShown = areGuidedFlowsAllowed || !isAiAssistGuidedFlowEnabled;

    const { openGuide, closeGuide, isGuideOpen, ...guideManagerInfo } = useGuideManager({
        callSid,
        clientChannelId,
        globalConfig,
        logger,
        partner,
        sendMessage,
        onAskAssist: focusAssistTab,
        sessionId,
        ssoAccessToken,
        taskMdn,
        employeeId: employeeId ?? "",
        isSolveGuidedFlowsEnabled: isSolveGuidedFlowsEnabled && areGuidedFlowsShown,
        apiBaseUrl: env.backendUrls.apiBaseUrl,
    });

    useAutomaticGuide({
        activeVoiceTask,
        isCustomerSession,
        areGuidedFlowsAllowed: areGuidedFlowsShown,
        isCustomerVerificationComplete,
        isGuideOpen,
        partner,
        closeGuide,
        openGuide,
        logger,
    });

    useGuideRecommendation({
        callSid,
        isGuideOpen,
        logger,
        openGuide,
        sessionId,
    });

    const { currentGuideName } = useGuideSession(sessionId);
    const onInlineGuideClose = useCallback(() => closeGuide("closeButton"), [closeGuide]);
    // Set only with `isAiAssistGuidedFlowEnabled`: the guide renders inline in AI Assist, not the Guided flows tab.
    const inlineGuide: InlineGuide | undefined = isAiAssistGuidedFlowEnabled
        ? {
              title: currentGuideName ?? "Guided Flows",
              onClose: onInlineGuideClose,
              toolboxCommand: guideManagerInfo.guideToolboxCommand,
              step: guideManagerInfo.guideStep,
          }
        : undefined;

    return { ...guideManagerInfo, isGuideOpen, closeGuide, inlineGuide };
}
